import { useEffect, useRef, useState } from 'react';
import { ArrowRight, Gauge } from 'lucide-react';

import { SoftCard } from '@/components/ui/soft-card';

const prefersReducedMotion = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const clampPct = (n) => Math.max(0, Math.min(100, n));

/**
 * Hero gauge for the breakdown card. The ring grows in from 0 (CSS transition,
 * robust even where rAF is throttled) and the number counts up to match.
 */
function HeroRing({ value, target }) {
	const size = 168;
	const stroke = 12;
	const r = (size - stroke) / 2;
	const c = 2 * Math.PI * r;
	const cx = size / 2;
	const cy = size / 2;

	const [ringPct, setRingPct] = useState(0);
	const [display, setDisplay] = useState(0);
	const fromRef = useRef(0);

	useEffect(() => {
		const start = fromRef.current;
		fromRef.current = value;
		const ringTimer = setTimeout(() => setRingPct(value / 100), 40);
		if (prefersReducedMotion() || start === value) {
			setDisplay(value);
			return () => clearTimeout(ringTimer);
		}
		let raf = 0;
		let done = false;
		const t0 = performance.now();
		const duration = 1100;
		const tick = (now) => {
			const t = Math.min(1, (now - t0) / duration);
			setDisplay(Math.round(start + (value - start) * (1 - Math.pow(1 - t, 3))));
			if (t < 1) raf = requestAnimationFrame(tick);
			else done = true;
		};
		raf = requestAnimationFrame(tick);
		const fallback = setTimeout(() => {
			if (!done) setDisplay(value);
		}, duration + 150);
		return () => {
			clearTimeout(ringTimer);
			clearTimeout(fallback);
			cancelAnimationFrame(raf);
		};
	}, [value]);

	const offset = c - Math.min(Math.max(ringPct, 0), 1) * c;
	const targetAngle = -Math.PI / 2 + 2 * Math.PI * (target / 100);
	const tickInner = r - stroke / 2 - 2;
	const tickOuter = r + stroke / 2 + 2;
	const tx1 = cx + Math.cos(targetAngle) * tickInner;
	const ty1 = cy + Math.sin(targetAngle) * tickInner;
	const tx2 = cx + Math.cos(targetAngle) * tickOuter;
	const ty2 = cy + Math.sin(targetAngle) * tickOuter;

	return (
		<div className="relative shrink-0" style={{ width: size, height: size }}>
			<svg width={size} height={size}>
				<g transform={`rotate(-90 ${cx} ${cy})`}>
					<circle cx={cx} cy={cy} r={r} stroke="hsl(var(--muted))" strokeWidth={stroke} fill="none" />
					<circle
						cx={cx}
						cy={cy}
						r={r}
						stroke="url(#lpHeroGrad)"
						strokeWidth={stroke}
						strokeLinecap="round"
						fill="none"
						strokeDasharray={c}
						strokeDashoffset={offset}
						style={{ transition: 'stroke-dashoffset 1000ms cubic-bezier(0.22, 1, 0.36, 1)' }}
					/>
				</g>
				<line x1={tx1} y1={ty1} x2={tx2} y2={ty2} stroke="hsl(var(--foreground))" strokeWidth={2} strokeLinecap="round" />
				<defs>
					<linearGradient id="lpHeroGrad" x1="0" y1="0" x2="1" y2="1">
						<stop offset="0%" stopColor="hsl(var(--primary))" />
						<stop offset="100%" stopColor="hsl(var(--action))" />
					</linearGradient>
				</defs>
			</svg>
			<div className="absolute inset-0 flex flex-col items-center justify-center">
				<div className="text-4xl font-extrabold tracking-tight text-foreground">{display}%</div>
				<div className="mt-0.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Today</div>
			</div>
		</div>
	);
}

function Bar({ label, pct, barClass, durationClass = 'duration-700' }) {
	return (
		<div className="flex items-center gap-2">
			<span className="w-16 shrink-0 text-xs text-muted-foreground">{label}</span>
			<div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
				<div className={`h-full transition-[width] ${durationClass} ease-out ${barClass}`} style={{ width: `${pct}%` }} />
			</div>
		</div>
	);
}

/**
 * Local Potential breakdown hero (NEA-5765) — the live score gauge beside the
 * "places people find you × products people can see" factors, ported from the
 * fresh-dashboard design and bound to the resolver. `onFixProducts` scrolls to
 * the Products section.
 */
export function ScoreBreakdown({
	score,
	target,
	channels,
	tools,
	links,
	switchedOnPct,
	readyToShowPct,
	onFixProducts,
	isConverted,
	billingLoading
}) {
	const chBar = channels.max > 0 ? clampPct((channels.active / channels.max) * 100) : 0;
	const tlBar = tools.max > 0 ? clampPct((tools.active / tools.max) * 100) : 0;
	const lnBar = links.max > 0 ? clampPct((links.active / links.max) * 100) : 0;

	return (
		<SoftCard tone="soft" className="!p-6 md:!p-8">
			<div className="flex flex-col items-center gap-8 md:flex-row md:items-start">
				<div className="flex flex-col items-center gap-4">
					<div className="inline-flex h-5 items-center gap-1.5 self-start text-[11px] font-semibold uppercase leading-none tracking-[0.14em] text-muted-foreground">
						<Gauge className="h-3.5 w-3.5 text-primary" />
						LOCAL POTENTIAL
					</div>
					<HeroRing value={score} target={target} />
					<p className="min-h-4 max-w-[180px] text-center text-xs leading-snug text-muted-foreground">
						{billingLoading ? (
							' '
						) : isConverted ? (
							'Keep improving your score.'
						) : (
							<>
								First goal: reach <span className="font-semibold text-foreground">{target}%</span> in your first 30 days.
							</>
						)}
					</p>
				</div>
				<div
					className="hidden w-px self-stretch bg-gradient-to-b from-transparent via-border to-transparent md:block"
					aria-hidden
				/>
				<div className="flex min-w-0 flex-1 flex-col gap-5">
					<div>
						<div className="inline-flex h-5 items-center gap-1.5 text-[11px] font-semibold uppercase leading-none tracking-[0.14em] text-muted-foreground">
							<Gauge className="h-3.5 w-3.5 text-primary" />
							SCORE BREAKDOWN
						</div>
						<p className="mt-1 text-sm text-muted-foreground">
							Your Local Potential Score is made up from the places where and how locals can shop with you, multiplied by how
							many of your products are ready to be shown.
						</p>
					</div>
					<div className="flex items-stretch gap-3">
						<div className="flex-1 rounded-2xl border border-border bg-card p-4">
							<div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
								PLACES PEOPLE FIND YOU
							</div>
							<div className="mt-1 text-3xl font-extrabold tabular-nums tracking-tight text-foreground">
								{Math.round(switchedOnPct)}%
							</div>
							<div className="mt-3 space-y-1.5">
								<Bar label="Channels" pct={chBar} barClass="bg-primary" />
								<Bar label="Tools" pct={tlBar} barClass="bg-action" />
								<Bar label="Links" pct={lnBar} barClass="bg-success" durationClass="duration-1000" />
							</div>
						</div>
						<div className="flex select-none items-center text-xl font-bold text-muted-foreground" aria-hidden>
							×
						</div>
						<div className="flex-1 rounded-2xl border border-border bg-card p-4">
							<div className="flex items-start justify-between gap-2">
								<div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
									PRODUCTS PEOPLE CAN SEE
								</div>
								{readyToShowPct < 100 && onFixProducts ? (
									<button
										type="button"
										onClick={onFixProducts}
										className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-warning px-3 py-1.5 text-xs font-semibold text-warning-foreground transition hover:opacity-90"
									>
										Fix <ArrowRight className="h-3.5 w-3.5" />
									</button>
								) : null}
							</div>
							<div className="mt-1 text-3xl font-extrabold tabular-nums tracking-tight text-foreground">
								{Math.round(readyToShowPct)}%
							</div>
							<div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-muted">
								<div
									className="h-full bg-warning transition-[width] duration-700 ease-out"
									style={{ width: `${clampPct(readyToShowPct)}%` }}
								/>
							</div>
							<p className="mt-2 text-xs leading-snug text-muted-foreground">
								The % of in-stock products that are searchable for local shoppers. Add at least a title to make them all
								appear.
							</p>
						</div>
					</div>
				</div>
			</div>
		</SoftCard>
	);
}
